(function (global, factory) {
  const ProductVideoRuntime = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = ProductVideoRuntime;
    return;
  }
  if (global.DrSknnProductVideos) {
    global.DrSknnProductVideos.refresh(global.document);
    return;
  }
  global.DrSknnProductVideos = new ProductVideoRuntime(global);
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  return class ProductVideoRuntime {
    constructor(environment) {
      this.window = environment;
      this.document = environment.document;
      this.entries = new Map();
      this.active = null;
      this.frame = null;
      this.motion = environment.matchMedia?.('(prefers-reduced-motion: reduce)');
      this.connection = environment.navigator?.connection;
      this.schedule = this.schedule.bind(this);
      this.onLoad = event => this.refresh(event.target);
      this.onUnload = event => this.removeWithin(event.target);
      this.onPageHide = () => this.entries.forEach(entry => this.pause(entry));
      this.document.addEventListener('visibilitychange', this.schedule);
      this.document.addEventListener('shopify:section:load', this.onLoad);
      this.document.addEventListener('shopify:section:unload', this.onUnload);
      environment.addEventListener('pagehide', this.onPageHide);
      environment.addEventListener('pageshow', this.schedule);
      if (this.motion?.addEventListener) this.motion.addEventListener('change', this.schedule);
      else this.motion?.addListener?.(this.schedule);
      this.connection?.addEventListener?.('change', this.schedule);
      if (environment.IntersectionObserver) {
        this.nearObserver = new environment.IntersectionObserver(rows => {
          rows.forEach(row => {
            const entry = this.entries.get(row.target);
            if (!entry) return;
            entry.near = row.isIntersecting;
            if (entry.near && !this.connection?.saveData) this.hydrate(entry);
          });
          this.schedule();
        }, { rootMargin: '320px 0px', threshold: 0 });
        this.visibleObserver = new environment.IntersectionObserver(rows => {
          rows.forEach(row => {
            const entry = this.entries.get(row.target);
            if (entry) entry.ratio = row.isIntersecting ? row.intersectionRatio : 0;
          });
          this.schedule();
        }, { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] });
      }
      this.refresh(this.document);
    }

    refresh(root) {
      const videos = Array.from(root.querySelectorAll?.('video[data-drsknn-product-video]') || []);
      if (root.matches?.('video[data-drsknn-product-video]')) videos.unshift(root);
      videos.forEach(video => {
        if (this.entries.has(video)) return;
        const wrapper = video.parentElement.closest('[data-drsknn-product-video]');
        if (!wrapper) return;
        const entry = {
          video, wrapper, button: wrapper.querySelector('[data-drsknn-video-play]'),
          status: wrapper.querySelector('[data-drsknn-video-status]'),
          ratio: 0, near: false, hydrated: false, userPaused: false, blocked: false,
          autoAllowed: video.dataset.autoplay !== 'false', expectedPlayEvents: [],
          manual: false, pending: false, token: 0, ownedPauses: 0, error: false, started: false, lastPauseOwned: false
        };
        // Begin silently. Subsequent deliberate volume/mute choices are never overwritten.
        video.muted = true;
        video.playsInline = true;
        video.controls = false; // Source is deferred; the compact Load action is available until hydration.
        video.setAttribute('aria-hidden', 'true');
        video.setAttribute('tabindex', '-1');
        video.removeAttribute('autoplay');
        entry.onPlay = () => {
          const managedPlay = entry.expectedPlayEvents.shift();
          if (video.paused) return; // Ignore a queued play event cancelled before it was delivered.
          entry.started = true;
          entry.lastPauseOwned = false;
          if (!managedPlay) {
            entry.manual = true;
            entry.userPaused = false;
            entry.blocked = false;
          }
          entry.blocked = false;
          this.clearError(entry);
          this.updateButton(entry);
          this.entries.forEach(other => { if (other !== entry) this.pause(other); });
          this.active = entry;
        };
        entry.onPause = () => {
          if (entry.ownedPauses > 0) entry.ownedPauses -= 1;
          else if (!video.ended) {
            entry.userPaused = true;
            entry.manual = false;
            entry.lastPauseOwned = false;
          }
          this.updateButton(entry);
          this.schedule();
        };
        entry.onEnded = () => {
          entry.userPaused = true;
          entry.manual = false;
          this.updateButton(entry);
        };
        entry.onError = () => {
          entry.token += 1;
          entry.pending = false;
          entry.error = true;
          entry.blocked = true;
          this.updateButton(entry);
          if (entry.status) entry.status.textContent = 'The video could not load. Select Retry video to try again.';
        };
        entry.onManualPlay = () => this.play(entry, true);
        video.addEventListener('play', entry.onPlay);
        video.addEventListener('pause', entry.onPause);
        video.addEventListener('ended', entry.onEnded);
        video.addEventListener('error', entry.onError);
        entry.button?.addEventListener('click', entry.onManualPlay);
        this.entries.set(video, entry);
        this.updateButton(entry);
        this.nearObserver?.observe(video);
        this.visibleObserver?.observe(video);
      });
      this.schedule();
    }

    hydrate(entry) {
      if (entry.hydrated) return;
      entry.video.querySelectorAll('source[data-src]').forEach(source => {
        source.setAttribute('src', source.getAttribute('data-src'));
      });
      if (entry.video.dataset.src) entry.video.src = entry.video.dataset.src;
      entry.hydrated = true;
      entry.video.controls = true;
      entry.video.preload = this.connection?.saveData ? 'none' : 'metadata';
      entry.video.load();
      entry.video.removeAttribute('aria-hidden');
      entry.video.setAttribute('tabindex', '0');
      this.updateButton(entry);
    }

    updateButton(entry) {
      if (!entry.button) return;
      // Native controls own normal play/pause/replay. This separate action exists only
      // when a deferred source needs loading or a rejected request needs explicit retry.
      const show = !entry.hydrated || entry.error || entry.blocked;
      const label = entry.error ? 'Retry video' : entry.blocked ? 'Play video' : 'Load video';
      entry.button.textContent = label;
      const title = entry.video.getAttribute?.('aria-label');
      entry.button.setAttribute('aria-label', title ? `${label}: ${title}` : label);
      if (!show && this.document.activeElement === entry.button) {
        entry.video.focus({ preventScroll: true });
      }
      entry.button.hidden = !show;
    }

    clearError(entry) {
      entry.error = false;
      if (entry.status) entry.status.textContent = '';
    }

    async play(entry, manual = false) {
      if (!this.entries.has(entry.video) || entry.pending) return;
      if (!manual && (!entry.autoAllowed || entry.userPaused || entry.blocked || this.motion?.matches || this.connection?.saveData)) return;
      if (manual) {
        entry.userPaused = false;
        entry.blocked = false;
        entry.manual = true;
        const rect = entry.video.getBoundingClientRect();
        const visibleWidth = Math.max(0, Math.min(rect.right, this.window.innerWidth) - Math.max(rect.left, 0));
        const visibleHeight = Math.max(0, Math.min(rect.bottom, this.window.innerHeight) - Math.max(rect.top, 0));
        entry.ratio = rect.width && rect.height ? visibleWidth * visibleHeight / (rect.width * rect.height) : entry.ratio;
        if (entry.error && entry.hydrated) entry.video.load();
        this.clearError(entry);
      }
      this.hydrate(entry);
      this.entries.forEach(other => { if (other !== entry) this.pause(other); });
      this.active = entry;
      const token = ++entry.token;
      entry.pending = true;
      if (entry.video.paused) entry.expectedPlayEvents.push({ token, manual });
      try {
        await entry.video.play();
        if (token === entry.token) this.updateButton(entry);
      } catch (error) {
        entry.expectedPlayEvents = entry.expectedPlayEvents.filter(event => event.token !== token);
        if (token !== entry.token) return; // An owned offscreen/background pause cancelled this request.
        entry.blocked = true; // No automatic retry loop; the separate fallback permits a deliberate retry.
        this.updateButton(entry);
        if (!['NotAllowedError', 'AbortError'].includes(error?.name) && entry.status) {
          entry.error = true;
          this.updateButton(entry);
          entry.status.textContent = 'The video could not start. Select Retry video to try again.';
        }
      } finally {
        if (token === entry.token) entry.pending = false;
      }
    }

    pause(entry) {
      if (entry.pending) {
        entry.token += 1;
        entry.pending = false;
      }
      if (!entry.video.paused) {
        // Count only real state transitions; the queued pause event may arrive after another play.
        entry.ownedPauses += 1;
        entry.lastPauseOwned = true;
        entry.video.pause();
      }
    }

    schedule() {
      if (this.frame !== null) return;
      this.frame = this.window.requestAnimationFrame(() => {
        this.frame = null;
        this.reconcile();
      });
    }

    reconcile() {
      this.entries.forEach(entry => {
        if (entry.video.isConnected === false) this.remove(entry);
        else {
          if (entry.started && entry.video.paused && !entry.pending && !entry.lastPauseOwned && !entry.video.ended) {
            entry.userPaused = true; // Respect a native pause even before its queued event is delivered.
          }
          if (entry.near && !this.connection?.saveData) this.hydrate(entry);
        }
      });
      let desired = null;
      if (!this.document.hidden) {
        // An explicit playback choice may continue while partially visible, including reduced-motion mode.
        if (this.active?.manual && !this.active.userPaused && !this.active.blocked && (!this.visibleObserver || this.active.ratio > 0)) {
          desired = this.active;
        } else if (!this.motion?.matches && !this.connection?.saveData && this.visibleObserver) {
          this.entries.forEach(entry => {
            if (!entry.autoAllowed || entry.ratio <= 0.5 || entry.userPaused || entry.blocked) return;
            if (!desired || entry.ratio > desired.ratio || (entry.ratio === desired.ratio && entry === this.active)) desired = entry;
          });
        }
      }
      this.entries.forEach(entry => { if (entry !== desired) this.pause(entry); });
      this.active = desired;
      if (desired && desired.video.paused && !desired.pending) this.play(desired, false);
    }

    removeWithin(root) {
      this.entries.forEach(entry => { if (root === entry.video || root.contains?.(entry.video)) this.remove(entry); });
    }

    remove(entry) {
      this.pause(entry);
      this.nearObserver?.unobserve(entry.video);
      this.visibleObserver?.unobserve(entry.video);
      entry.video.removeEventListener('play', entry.onPlay);
      entry.video.removeEventListener('pause', entry.onPause);
      entry.video.removeEventListener('ended', entry.onEnded);
      entry.video.removeEventListener('error', entry.onError);
      entry.button?.removeEventListener('click', entry.onManualPlay);
      this.entries.delete(entry.video);
      if (this.active === entry) this.active = null;
    }

    destroy() {
      this.entries.forEach(entry => this.remove(entry));
      this.nearObserver?.disconnect();
      this.visibleObserver?.disconnect();
      if (this.frame !== null) this.window.cancelAnimationFrame(this.frame);
      this.document.removeEventListener('visibilitychange', this.schedule);
      this.document.removeEventListener('shopify:section:load', this.onLoad);
      this.document.removeEventListener('shopify:section:unload', this.onUnload);
      this.window.removeEventListener('pagehide', this.onPageHide);
      this.window.removeEventListener('pageshow', this.schedule);
      if (this.motion?.removeEventListener) this.motion.removeEventListener('change', this.schedule);
      else this.motion?.removeListener?.(this.schedule);
      this.connection?.removeEventListener?.('change', this.schedule);
    }
  };
});
